import React, { useContext, useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddCoursePopup from './MandatoryTrainingPopup'
import API from '../../../services/API'
import CheckIcon from '@mui/icons-material/Check'
import SchoolIcon from '@mui/icons-material/School'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import { Stack } from '@mui/material'
import { GlobalContext } from '../../../App'
import {
  addCatalogueCourse,
  errorMessage,
  getCatalogue,
  isOk,
} from '../../../training/helpers/trainingApi'
import {
  catalogueCourseFor,
  expiryLabel,
  pathwayShortLabel,
  requiredPathwayTitle,
} from '../../../training/components/admin/catalogueHelpers'

const MandatoryTraining = () => {
  const [addCoursePopupOpen, setAddCoursePopupOpen] = useState(false)
  const [dataLoading, setDataLoading] = useState(true)
  const [courseList, setCourseList] = useState([])
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [roles, setRoles] = useState([])
  // { available, courses }; null until loaded (or if it could not be)
  const [catalogue, setCatalogue] = useState(null)
  const [adding, setAdding] = useState(null)
  const { setToast } = useContext(GlobalContext)

  const loadList = () => {
    API.get('/mandatorytraininglist')
      .then((res) => {
        if (res.status === 200) {
          setCourseList(res.data)
        }
      })
      .finally(() => setDataLoading(false))
    getCatalogue().then((res) => {
      if (isOk(res)) setCatalogue(res.data)
    })
  }

  useEffect(() => {
    loadList()
    API.get('/settings?select=roles').then((res) => {
      if (res.status === 200) setRoles(res.data.roles ?? [])
    })
  }, [])

  const available = !!catalogue?.available
  const catalogueCourses = catalogue?.courses ?? []
  const unlinked = catalogueCourses.filter((course) => !course.linkedCourse)
  // Courses can be offered before AmbuNet's clinical sign-off and before CPD
  // accreditation; say so plainly wherever they are switched on.
  const awaitingSignOff = catalogueCourses.some((course) => !course.signedOff)
  const awaitingAccreditation = catalogueCourses.some(
    (course) => !course.accredited,
  )
  const trialNotice =
    available && (awaitingSignOff || awaitingAccreditation)
      ? [
          awaitingSignOff
            ? "Some courses have not yet had AmbuNet's final clinical sign-off."
            : null,
          awaitingAccreditation
            ? 'They are not yet accredited by The CPD Certification Service.'
            : null,
        ]
          .filter(Boolean)
          .join(' ')
      : null
  const linkedTo = (row) =>
    available ? catalogueCourseFor(catalogueCourses, row.catalogueCode) : null

  const handleAddFromCatalogue = async (course) => {
    setAdding(course.code)
    const res = await addCatalogueCourse(course.code)
    setAdding(null)
    if (isOk(res)) {
      setToast({
        message: `${res.data?.title ?? course.title} is now in your courses. Choose which roles need it.`,
      })
      loadList()
    } else {
      setToast({
        message: errorMessage(res, 'Unable to add this course.'),
        severity: 'error',
      })
    }
  }

  return (
    <Stack gap={1.5}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="title2" fontSize={20} padding={1}>
          Mandatory Training
        </Typography>
        <Button
          variant="contained"
          color="highlight"
          onClick={() => {
            setSelectedCourse(null)
            setAddCoursePopupOpen(true)
          }}
        >
          Add Course
        </Button>
      </Box>
      {trialNotice && (
        <Alert severity="warning">
          <AlertTitle>E-learning courses are on trial</AlertTitle>
          {trialNotice} You can use them now, but check that the content meets
          your needs before you rely on it for compliance. Certificates issued
          before a course is accredited will not carry the CPD Certified mark.
        </Alert>
      )}
      <CustomDataGrid
        rows={courseList}
        getRowId={(params) => params._id}
        loading={dataLoading}
        columns={[
          {
            field: 'title',
            headerName: 'Course Name',
            minWidth: 600,
            flex: 1,
            renderCell: ({ row }) => (
              <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                <Typography variant="title2" fontSize="1rem" noWrap>
                  {row.title}
                </Typography>
                {linkedTo(row) && (
                  <Tooltip
                    title={`Staff can take this as e-learning: ${linkedTo(row).title}`}
                  >
                    <Chip
                      size="small"
                      color="anMainBlue"
                      icon={<SchoolIcon />}
                      label={`E-learning · ${row.catalogueCode}`}
                      sx={{ borderRadius: 1, flexShrink: 0 }}
                    />
                  </Tooltip>
                )}
              </Stack>
            ),
          },
          {
            field: 'frequency',
            headerName: 'Frequency',
          },
          ...roles.map((role) => ({
            field: role,
            headerName: role,
            minWidth: 110,
            sortable: false,
            filterable: false,
            valueGetter: (value, row) => {
              const requirement = row.requiredBy?.[role]
              if (!requirement) return ''
              return (
                requiredPathwayTitle(requirement, linkedTo(row)) ?? 'Required'
              )
            },
            renderCell: ({ row, value }) =>
              !!row.requiredBy?.[role] && (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                  aria-label={value}
                >
                  <CheckIcon color="error" titleAccess="Required" />
                  {value !== 'Required' && (
                    <Typography
                      variant="caption"
                      lineHeight={1.1}
                      textAlign="center"
                      sx={{ whiteSpace: 'normal' }}
                    >
                      {value}
                    </Typography>
                  )}
                </Stack>
              ),
            headerAlign: 'center',
            align: 'center',
          })),
        ]}
        onRowClick={({ row }) => {
          setSelectedCourse(row)
          setAddCoursePopupOpen(true)
        }}
      />

      {available && unlinked.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack gap={1.5}>
            <Box>
              <Typography variant="title3" fontWeight={700}>
                E-learning courses available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Courses your staff can complete inside AmbuNet. Add one to your
                courses, then choose which roles need it. If you already have a
                course of your own for the same subject, open it above and use
                "Link to e-learning course" instead.
              </Typography>
            </Box>
            {unlinked.map((course) => (
              <Stack
                key={course.code}
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                gap={1}
                sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}
              >
                <Box minWidth={0}>
                  <Typography fontWeight={600}>
                    {course.title}{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      {course.code}
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {course.pathways
                      .map((pathway) =>
                        [
                          course.pathways.length > 1 ? pathwayShortLabel(pathway) : null,
                          pathway.estimatedMinutes
                            ? `about ${pathway.estimatedMinutes} minutes`
                            : null,
                          `renewed every ${expiryLabel(pathway.expiryMonths)}`,
                        ]
                          .filter(Boolean)
                          .join(', '),
                      )
                      .join(' · ')}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="third"
                  size="small"
                  onClick={() => handleAddFromCatalogue(course)}
                  disabled={!!adding}
                  sx={{ flexShrink: 0 }}
                >
                  {adding === course.code ? 'Adding...' : 'Add to our courses'}
                </Button>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {catalogue && !available && (
        <Typography variant="body2" color="text.secondary">
          E-learning courses are available on Premium and above.
        </Typography>
      )}

      <AddCoursePopup
        addCoursePopupOpen={addCoursePopupOpen}
        setAddCoursePopupOpen={setAddCoursePopupOpen}
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        roles={roles}
        catalogue={catalogue}
        reload={loadList}
      />
    </Stack>
  )
}

export default MandatoryTraining
